import { useEffect, useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';

import {
    CheckField,
    FormAlert,
    PasswordField,
    SelectField,
    SubmitButton,
    TextField,
    UserComboField,
    UserSelect,
    vEmail,
    vOptional,
    vRequired,
    type SelectOption,
    type UserPick,
} from '../form';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { get, post, statusOf } from '../../lib/api';
import { ApplicationType } from '../../lib/applications';
import { ModeToggle, type MappingMode } from './ModeToggle';
import { useUserInstances } from './useUserInstances';

/** Map the GetSmartstoreUsers response (UserRoleView) into pickable users. */
const toUserPicks = (
    data: { value: string; firstName?: string; lastName?: string; phoneNumber?: string }[] | undefined,
): UserPick[] =>
    (data ?? []).map((u) => ({
        value: u.value,
        name: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim(),
        email: u.value,
        phone: u.phoneNumber ?? '',
    }));

const baseFields = {
    user: vRequired(),
    userPassword: vRequired(),
    companyId: vRequired(),
    // CS00 firmapassord is often blank — allow empty; backend sends companypwd= as string.empty.
    companyPassword: vOptional(),
    storeId: vRequired(),
    isJasper: z.boolean(),
    jasperName: z.string().optional().or(z.literal('')),
};
const jasperRefine = (d: { isJasper: boolean; jasperName?: string }) =>
    !d.isJasper || !!(d.jasperName && d.jasperName.length > 0);
const jasperRefineOpts = { message: 'validation.required', path: ['jasperName'] };

const addSchema = z
    .object({ applicationName: vRequired(), userName: vEmail, ...baseFields })
    .refine(jasperRefine, jasperRefineOpts);
type AddValues = z.infer<typeof addSchema>;

const editSchema = z.object(baseFields).refine(jasperRefine, jasperRefineOpts);
type EditValues = z.infer<typeof editSchema>;

function smartstoreError(status: number | undefined, t: (k: string) => string): string {
    switch (status) {
        case 406:
            return t('mappings.smartstore.userNotFound');
        case 422:
            return t('mappings.smartstore.userExists');
        case 400:
            return t('mappings.smartstore.domainInvalid');
        case 409:
            return t('mappings.smartstore.mappingExists');
        default:
            return t('mappings.smartstore.serverError');
    }
}

function SmartStoreFields({ jasperServers }: { jasperServers: SelectOption[] }) {
    const { t } = useTranslation();
    const isJasper = useWatch({ name: 'isJasper' });
    return (
        <>
            <TextField name="user" label={t('mappings.smartstore.user')} placeholder="WTEST" required />
            <PasswordField
                name="userPassword"
                label={t('mappings.smartstore.userPassword')}
                autoComplete="new-password"
                required
            />
            <TextField name="companyId" label={t('mappings.smartstore.companyId')} placeholder="10001" required />
            <PasswordField
                name="companyPassword"
                label={t('mappings.smartstore.companyPassword')}
                autoComplete="new-password"
            />
            <TextField name="storeId" label={t('mappings.smartstore.storeId')} placeholder="001" required />
            <CheckField name="isJasper" label={t('mappings.smartstore.addJasper')} />
            {isJasper && (
                <SelectField
                    name="jasperName"
                    label={t('mappings.smartstore.jasperServer')}
                    options={jasperServers}
                    required
                />
            )}
        </>
    );
}

export function SmartStoreMapping() {
    const { t } = useTranslation();
    const [mode, setMode] = useState<MappingMode>('add');
    const [instances, setInstances] = useState<SelectOption[]>([]);
    const [jasperServers, setJasperServers] = useState<SelectOption[]>([]);
    const [users, setUsers] = useState<UserPick[]>([]);
    const [allUsers, setAllUsers] = useState<SelectOption[]>([]);
    const [serverError, setServerError] = useState('');
    const [success, setSuccess] = useState('');

    const [editUser, setEditUser] = useState('');
    const { userInstances, editInstance, setEditInstance } = useUserInstances(
        mode,
        editUser,
        ApplicationType.Smartstore,
    );
    const [loaded, setLoaded] = useState(false);
    const [loadedUserName, setLoadedUserName] = useState('');
    const [showDelete, setShowDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const addMethods = useForm<AddValues>({
        resolver: zodResolver(addSchema),
        defaultValues: {
            applicationName: '',
            userName: '',
            user: '',
            userPassword: '',
            companyId: '',
            companyPassword: '',
            storeId: '',
            isJasper: false,
            jasperName: '',
        },
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    });
    const editMethods = useForm<EditValues>({
        resolver: zodResolver(editSchema),
        defaultValues: {
            user: '',
            userPassword: '',
            companyId: '',
            companyPassword: '',
            storeId: '',
            isJasper: false,
            jasperName: '',
        },
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    });

    useEffect(() => {
        post<string[]>('home/getApplicationNames', { appType: ApplicationType.Smartstore })
            .then((r) => setInstances((r.data ?? []).map((d) => ({ value: d, label: d })).sort((a, b) => a.label.localeCompare(b.label, 'nb'))))
            .catch(() => undefined);
        post<string[]>('home/getApplicationNames', { appType: ApplicationType.Jasper })
            .then((r) => setJasperServers((r.data ?? []).map((d) => ({ value: d, label: d })).sort((a, b) => a.label.localeCompare(b.label, 'nb'))))
            .catch(() => undefined);
        get<{ value: string; firstName: string; lastName: string; phoneNumber: string }[]>('Home/GetSmartstoreUsers')
            .then((r) => setUsers(toUserPicks(r.data)))
            .catch(() => undefined);
        // All SSO users, for the Add form's searchable user picker. (Edit mode keeps
        // GetSmartstoreUsers above — only users that already have a mapping.)
        get<{ userName: string; email: string; firstName: string; lastName: string }[]>('Home/GetAllUsers')
            .then((r) =>
                setAllUsers(
                    (r.data ?? [])
                        .map((u) => {
                            const value = u.userName || u.email;
                            const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
                            return { value, label: name ? `${name} (${value})` : value };
                        })
                        .sort((a, b) => a.label.localeCompare(b.label, 'nb')),
                ),
            )
            .catch(() => undefined);
    }, []);

    useEffect(() => {
        if (mode !== 'edit' || !editInstance || !editUser) return;
        setSuccess('');
        setServerError('');
        post<{
            userName: string;
            user: string;
            userPassword: string;
            companyId: string;
            companyPassword: string;
            storeId: string;
            isJasper: boolean;
            jasperName: string;
        }>('Home/LoadSelectedSmartStore', { Label: editUser, Value: editInstance })
            .then((res) => {
                setLoadedUserName(res.data.userName ?? '');
                editMethods.reset({
                    user: res.data.user ?? '',
                    userPassword: res.data.userPassword ?? '',
                    companyId: res.data.companyId ?? '',
                    companyPassword: res.data.companyPassword ?? '',
                    storeId: res.data.storeId ?? '',
                    isJasper: !!res.data.isJasper,
                    jasperName: res.data.jasperName ?? '',
                });
                setLoaded(true);
            })
            .catch(() => {
                setLoaded(false);
                setServerError(t('mappings.smartstore.loadError'));
            });
        // Reload only when the edit selection changes.
    }, [mode, editInstance, editUser]);

    // Clears the edit selections/fields but leaves status messages alone, so
    // the success banner survives the post-update reset.
    function clearEdit() {
        setEditUser('');
        setEditInstance('');
        setLoaded(false);
        setLoadedUserName('');
        editMethods.reset();
    }

    function switchMode(next: MappingMode) {
        setMode(next);
        setServerError('');
        setSuccess('');
        addMethods.reset();
        clearEdit();
    }

    async function onAdd(values: AddValues) {
        setServerError('');
        setSuccess('');
        try {
            await post('home/AddSmartStore', values);
            setSuccess(t('mappings.smartstore.registered'));
            addMethods.reset();
        } catch (err) {
            setServerError(smartstoreError(statusOf(err), t));
        }
    }

    async function onUpdate(values: EditValues) {
        setServerError('');
        setSuccess('');
        try {
            await post('Home/UpdateSmartstore', {
                applicationName: editInstance,
                userName: loadedUserName,
                ssoUserName: editUser,
                ...values,
            });
            setSuccess(t('mappings.smartstore.updated'));
            clearEdit();
        } catch {
            setServerError(t('mappings.smartstore.updateError'));
        }
    }

    async function confirmDelete() {
        setDeleting(true);
        setServerError('');
        try {
            await post('home/DeleteMapping', { email: editUser, applicationName: editInstance });
            setShowDelete(false);
            setSuccess(t('mappings.deleted'));
            // The user may no longer have a SmartStore mapping — refresh the list.
            get<{ value: string; firstName: string; lastName: string; phoneNumber: string }[]>('Home/GetSmartstoreUsers')
                .then((r) => setUsers(toUserPicks(r.data)))
                .catch(() => undefined);
            clearEdit();
        } catch {
            setShowDelete(false);
            setServerError(t('mappings.deleteError'));
        } finally {
            setDeleting(false);
        }
    }

    return (
        <div className="pt-3">
            <p className="text-body-secondary">{t('mappings.smartstore.description')}</p>
            <ModeToggle mode={mode} onChange={switchMode} idPrefix="smartstore" />

            {success && (
                <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}
            <FormAlert onClose={() => setServerError('')}>{serverError || undefined}</FormAlert>

            {mode === 'add' ? (
                <FormProvider {...addMethods}>
                    <Form noValidate onSubmit={addMethods.handleSubmit(onAdd)} style={{ maxWidth: '34rem' }}>
                        <SelectField
                            name="applicationName"
                            label={t('mappings.smartstore.instance')}
                            options={instances}
                            required
                        />
                        <UserComboField
                            name="userName"
                            label={t('mappings.smartstore.userName')}
                            options={allUsers}
                            placeholder={t('mappings.smartstore.userNamePlaceholder')}
                            required
                        />
                        <SmartStoreFields jasperServers={jasperServers} />
                        <SubmitButton loading={addMethods.formState.isSubmitting}>
                            {t('mappings.smartstore.register')}
                        </SubmitButton>
                    </Form>
                </FormProvider>
            ) : (
                <div style={{ maxWidth: '34rem' }}>
                    <Form.Group className="mb-3">
                        <Form.Label htmlFor="ss-edit-user">{t('mappings.smartstore.selectUser')}</Form.Label>
                        <UserSelect
                            inputId="ss-edit-user"
                            users={users}
                            value={editUser}
                            onChange={(v) => {
                                setEditUser(v);
                                setEditInstance('');
                                setLoaded(false);
                            }}
                        />
                    </Form.Group>
                    {userInstances.length > 0 && (
                        <Form.Group className="mb-3">
                            <Form.Label htmlFor="ss-edit-instance">{t('mappings.smartstore.instance')}</Form.Label>
                            <Select
                                inputId="ss-edit-instance"
                                options={userInstances}
                                classNamePrefix="dkn-rs"
                                placeholder={t('common.selectPlaceholder')}
                                noOptionsMessage={() => t('common.noOptions')}
                                value={userInstances.find((o) => o.value === editInstance) ?? null}
                                onChange={(o) => setEditInstance((o as SelectOption | null)?.value ?? '')}
                            />
                        </Form.Group>
                    )}

                    {loaded && (
                        <FormProvider {...editMethods}>
                            <Form noValidate onSubmit={editMethods.handleSubmit(onUpdate)}>
                                <SmartStoreFields jasperServers={jasperServers} />
                                <div className="d-flex gap-2">
                                    <SubmitButton loading={editMethods.formState.isSubmitting}>
                                        {t('mappings.smartstore.update')}
                                    </SubmitButton>
                                    <Button
                                        variant="outline-danger"
                                        type="button"
                                        onClick={() => setShowDelete(true)}
                                    >
                                        {t('mappings.delete')}
                                    </Button>
                                </div>
                            </Form>
                        </FormProvider>
                    )}
                </div>
            )}

            <ConfirmDialog
                show={showDelete}
                title={t('mappings.deleteTitle')}
                confirmLabel={t('mappings.proceed')}
                onCancel={() => setShowDelete(false)}
                onConfirm={confirmDelete}
                confirmDisabled={deleting}
            >
                {t('mappings.confirmDelete')}
            </ConfirmDialog>
        </div>
    );
}
